import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../api/client';
import type { AuditEvent, AuditListResponse } from '../api/types';

// Known event names, for the filter dropdown. '' = no filter.
const EVENTS = [
  '',
  'create',
  'retrieve_ok',
  'retrieve_404',
  'retrieve_410',
  'login_ok',
  'login_fail',
  'password_change',
  'password_reset',
  'user_create',
  'user_delete',
];

export default function AuditPage() {
  const [rows, setRows] = useState<AuditEvent[]>([]);
  const [event, setEvent] = useState('');
  const [tokenHash, setTokenHash] = useState('');
  const [limit, setLimit] = useState('100');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function load() {
    setError('');
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (event) params.set('event', event);
      if (tokenHash) params.set('token_hash', tokenHash);
      const lim = parseInt(limit, 10);
      if (Number.isFinite(lim) && lim > 0) params.set('limit', String(lim));
      const qs = params.toString();
      const r = await api<AuditListResponse>(`/admin/audit${qs ? `?${qs}` : ''}`);
      setRows(r.rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'load failed');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
  }, []);

  function applyFilters(e: FormEvent) {
    e.preventDefault();
    void load();
  }

  return (
    <div className="card">
      <h1>audit log</h1>
      <p className="muted" style={{ marginBottom: 16 }}>
        Every create / retrieve / login / user-management event. Records{' '}
        <code>sha256(token)</code> and a short detail — never the secret value
        or the token literal.
      </p>
      {error && <div className="error-banner">{error}</div>}
      <form onSubmit={applyFilters}>
        <div className="row">
          <div>
            <label className="label">Event</label>
            <select value={event} onChange={(e) => setEvent(e.target.value)}>
              {EVENTS.map((ev) => (
                <option key={ev} value={ev}>
                  {ev || 'all events'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Token hash (prefix)</label>
            <input
              value={tokenHash}
              onChange={(e) => setTokenHash(e.target.value)}
              placeholder="e.g. 3f9a2c…"
            />
          </div>
          <div>
            <label className="label">Limit</label>
            <input
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              inputMode="numeric"
              pattern="[0-9]+"
            />
          </div>
        </div>
        <div className="actions">
          <button type="submit" disabled={loading}>
            {loading ? 'loading…' : 'apply filters'}
          </button>
        </div>
      </form>

      {rows.length === 0 ? (
        <p className="muted">no matching events</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>when</th>
              <th>event</th>
              <th>user</th>
              <th>ip</th>
              <th>token_hash</th>
              <th>detail</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="muted">{fmtDate(r.at)}</td>
                <td>{r.event}</td>
                <td className="muted">{r.user_id ?? '—'}</td>
                <td className="muted">{r.ip ?? '—'}</td>
                <td>{r.token_hash ? <code>{r.token_hash.slice(0, 12)}…</code> : '—'}</td>
                <td className="muted">{r.detail ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function fmtDate(ts: number): string {
  return new Date(ts * 1000).toLocaleString();
}
